import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  StarIcon,
  MapPinIcon,
  GlobeIcon,
  NavigationArrowIcon,
  HeartIcon,
  CableCarIcon,
  PathIcon,
  ImagesIcon,
  CheckCircleIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, STYLE_LABELS, ACCESS_LABELS, DIFFICULTY } from "@/lib/apiClient";
import {
  currentDestinationStatusLabel,
  directionsUrlForDestination,
  primaryAccessPointLabel,
  isTrailCountOnlyRecommendation,
} from "@/lib/destinationDetail";
import { destinationDecisionSnapshot, destinationMediaAssets } from "@/lib/destinationPresentation";
import { destinationCoverImage } from "@/lib/destinationCover";
import {
  filterDestinationTrails,
  initialTrailInteractionState,
  trailInteractionReducer,
} from "@/lib/trailMapListSync";
import {
  TRAIL_CATALOG_GROUPS,
  filterTrailsByCatalogGroup,
  groupTrailsForCatalog,
  trailCatalogCounts,
} from "@/lib/trailCatalog";
import { Navbar } from "@/components/Navbar";
import { MapView } from "@/components/MapView";
import { DifficultyLegend } from "@/components/DifficultyBar";
import { TrailCatalog } from "@/components/TrailCatalog";
import { DestinationWeatherForecast } from "@/components/DestinationWeatherForecast";
import { DestinationReviews } from "@/components/DestinationReviews";
import { DestinationEvents } from "@/components/DestinationEvents";
import {
  DestinationCommunityActions,
  DestinationPracticalInfo,
  DestinationVisitDecisionPanel,
} from "@/components/DestinationDecisionSections";
import { CommunityMediaGallery } from "@/components/CommunityMediaGallery";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { ShareMenu } from "@/components/ShareMenu";
import { SuggestTrail } from "@/components/SuggestTrail";
import { parkSharePayload } from "@/lib/share";
import { parkPath, trailPath } from "@/lib/routes";
import { trackEvent } from "@/lib/analytics";
import { Seo, buildParkSchema } from "@/components/Seo";
import { DestinationStatusTrustPanel } from "@/components/StatusTrust";
import { PlanThisRideButton } from "@/components/PlanThisRideButton";
import { ParkDecisionSnapshot } from "@/components/ParkDecisionSnapshot";

const DIFFS = ["easy", "intermediate", "advanced", "expert"];
const MAP_TRAILS_ENABLED = process.env.REACT_APP_DESTINATION_MAP_TRAILS_ENABLED !== "false";

const STATUS_STYLES = {
  OPEN_NOW: "bg-emerald-500 text-black",
  CLOSED_NOW: "bg-red-500 text-white",
  RESTRICTED: "bg-amber-400 text-black",
  UNKNOWN: "bg-zinc-700 text-white",
};

export default function ParkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isFavorite, toggleFavorite } = useAuth();
  const [park, setPark] = useState(null);
  const [destinationState, setDestinationState] = useState(null);
  const [visitedBusy, setVisitedBusy] = useState(false);
  const [coverMediaUrl, setCoverMediaUrl] = useState(null);
  const [diffFilter, setDiffFilter] = useState([]);
  const [catalogGroupFilter, setCatalogGroupFilter] = useState("all");
  const [trailInteraction, dispatchTrailInteraction] = useReducer(
    trailInteractionReducer,
    initialTrailInteractionState,
  );
  const trailRowRefs = useRef({});
  const viewedParkIdRef = useRef(null);
  const handleBack = () => {
    const historyIndex = window.history.state?.idx;
    if (typeof historyIndex === "number" && historyIndex > 0) navigate(-1);
    else navigate("/", { replace: true });
  };
  const destinationSections = [
    { id: "overview", label: "Overview" },
    { id: "plan-your-ride", label: "Conditions" },
    ...(MAP_TRAILS_ENABLED ? [{ id: "trails", label: "Trails" }] : []),
    { id: "community-media", label: "Media" },
    { id: "practical-info", label: "Info" },
    { id: "reviews", label: "Reviews" },
  ];

  useEffect(() => {
    let cancelled = false;
    window.scrollTo(0, 0);
    setPark(null);
    setCoverMediaUrl(null);

    api.get(`/parks/${id}`).then(async ({ data }) => {
      if (cancelled) return;
      setPark(data);
      if (viewedParkIdRef.current !== data.id) {
        viewedParkIdRef.current = data.id;
        trackEvent("park_view", {
          park_id: String(data.id),
          park_name: data.name || "",
          country: data.country || "",
          destination_type: data.type || "",
          trail_count: data.trail_count ?? 0,
        });
      }

      const canonicalPath = parkPath(data);
      if (canonicalPath !== `/park/${id}`) {
        navigate(canonicalPath, { replace: true });
      }

      try {
        const mediaResult = await api.get("/media", {
          params: { destination_id: data.id, filter: "PHOTOS" },
        });
        if (cancelled) return;
        const explicitCover = mediaResult.data?.items?.find((item) => item.is_cover && item.media_type === "PHOTO");
        setCoverMediaUrl(explicitCover?.url || null);
      } catch {
        // Destination media is optional.
      }
    }).catch(() => {
      if (cancelled) return;
      toast.error(translateLabel("Park not found"));
      navigate("/");
    });

    return () => { cancelled = true; };
  }, [id, navigate]);

  useEffect(() => {
    if (!user || !park?.id) {
      setDestinationState(null);
      return;
    }
    api.get(`/destination-states/${park.id}`)
      .then(({ data }) => setDestinationState(data))
      .catch(() => setDestinationState(null));
  }, [park?.id, user]);

  const difficultyFilteredTrails = useMemo(
    () => filterDestinationTrails(park?.trails ?? [], diffFilter, []),
    [park, diffFilter],
  );
  const catalogCounts = useMemo(
    () => trailCatalogCounts(difficultyFilteredTrails),
    [difficultyFilteredTrails],
  );
  const allCatalogCounts = useMemo(
    () => trailCatalogCounts(park?.trails ?? []),
    [park],
  );
  const catalogFilterOptions = useMemo(
    () => TRAIL_CATALOG_GROUPS.filter(({ id: groupId }) => catalogCounts[groupId] > 0),
    [catalogCounts],
  );
  const trails = useMemo(
    () => filterTrailsByCatalogGroup(difficultyFilteredTrails, catalogGroupFilter),
    [difficultyFilteredTrails, catalogGroupFilter],
  );
  const trailGroups = useMemo(() => groupTrailsForCatalog(trails), [trails]);
  const visibleTrailIds = useMemo(() => trails.map(({ id: trailId }) => trailId), [trails]);

  useEffect(() => {
    dispatchTrailInteraction({ type: "reconcile", visibleTrailIds });
  }, [visibleTrailIds]);

  useEffect(() => {
    if (catalogGroupFilter === "all") return;
    if ((catalogCounts[catalogGroupFilter] ?? 0) > 0) return;
    setCatalogGroupFilter("all");
  }, [catalogCounts, catalogGroupFilter]);

  const navigateToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!park) {
    return (
      <div className="min-h-screen bg-[#09090b] pt-16">
        <Navbar scrollsWithPage brandVariant="mark" />
        <Skeleton className="h-[45vh] w-full bg-white/5" />
        <div className="mx-auto max-w-5xl space-y-4 px-4 py-6 md:px-6">
          <Skeleton className="h-10 w-1/2 bg-white/5" />
          <Skeleton className="h-40 w-full bg-white/5" />
        </div>
      </div>
    );
  }

  const canonicalPath = parkPath(park);
  const seoDescription = park.description || park.summary || `${park.name} is a mountain bike destination in ${[park.city, translateLabel(park.country)].filter(Boolean).join(", ")}.`;
  const seoImage = coverMediaUrl || destinationCoverImage(park, destinationMediaAssets(park)[0]?.src) || park.image;

  const fav = isFavorite(park.id, "park");
  const status = park.current_status || "UNKNOWN";
  const mediaAssets = destinationMediaAssets(park);
  const verifiedHeroImage = mediaAssets[0]?.src;
  const hasRealHeroImage = Boolean(coverMediaUrl || verifiedHeroImage || park.image);
  const heroImage = destinationCoverImage(park, coverMediaUrl || verifiedHeroImage);
  const accessLabel = park.access?.length
    ? park.access.map((a) => translateLabel(ACCESS_LABELS[a] || a)).join(" · ")
    : null;
  const stylesLabel = park.riding_styles?.length
    ? park.riding_styles.map((s) => translateLabel(STYLE_LABELS[s] || s)).join(" · ")
    : null;
  const selectedTrail = trails.find(({ id: trailId }) => trailId === trailInteraction.selectedTrailId);
  const directionsUrl = directionsUrlForDestination(park);
  const accessPointLabel = primaryAccessPointLabel(park);
  const trailCount = park.trail_count ?? 0;
  const visitDecision = destinationDecisionSnapshot(park, { accessLabel });
  const glanceItems = [
    { label: translateLabel("{count} trails", { count: trailCount }), tone: "lime" },
    allCatalogCounts.downhill > 0 ? { label: translateLabel("{count} downhill", { count: allCatalogCounts.downhill }), tone: "red" } : null,
    allCatalogCounts.flow > 0 ? { label: translateLabel("{count} flow", { count: allCatalogCounts.flow }), tone: "green" } : null,
    { label: visitDecision.uplift.value, tone: visitDecision.uplift.tone },
    visitDecision.ebike.value !== "Not applicable" ? { label: translateLabel("E-bike · {value}", { value: translateLabel(visitDecision.ebike.value) }), tone: visitDecision.ebike.tone } : null,
  ].filter(Boolean).slice(0, 5);
  const recommendationReason = park.recommendation_reason?.trim();
  const showRecommendationReason = Boolean(
    recommendationReason && !isTrailCountOnlyRecommendation(recommendationReason, trailCount),
  );

  const handleFav = async () => {
    const res = await toggleFavorite(park.id, "park");
    if (res.needAuth) {
      toast(translateLabel("Sign in to save"));
      navigate(`/login?returnTo=${encodeURIComponent(parkPath(park))}`);
    } else if (res.added) {
      trackEvent("favorite_added", { content_type: "park", item_id: String(park.id), item_name: park.name || "" });
      toast.success(translateLabel("Added to Want to Ride"));
    } else {
      trackEvent("favorite_removed", { content_type: "park", item_id: String(park.id), item_name: park.name || "" });
      toast(translateLabel("Removed"));
    }
  };

  const handleVisited = async () => {
    if (!user) {
      toast(translateLabel("Sign in to mark parks as visited"));
      navigate(`/login?returnTo=${encodeURIComponent(parkPath(park))}`);
      return;
    }

    const nextVisited = !Boolean(destinationState?.visited);
    setVisitedBusy(true);
    try {
      const { data } = await api.post(`/destination-states/${park.id}`, { visited: nextVisited });
      setDestinationState(data);
      trackEvent("park_visited_set", {
        park_id: String(park.id),
        park_name: park.name || "",
        visited: nextVisited,
      });
      toast.success(nextVisited ? "Marked as visited" : "Removed from Visited");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update Visited");
    } finally {
      setVisitedBusy(false);
    }
  };

  const handleTrailSelect = (trailId, source) => {
    trackEvent("trail_select", {
      trail_id: String(trailId),
      park_id: String(park.id),
      source,
    });
    dispatchTrailInteraction({ type: "select", trailId, source });
    if (source === "map") {
      window.requestAnimationFrame(() => {
        trailRowRefs.current[trailId]?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
      });
    }
  };

  const handleTrailHover = (trailId) => {
    dispatchTrailInteraction({ type: "hover", trailId });
  };

  const overview = [
    { label: "Destination", value: park.type, icon: MapPinIcon },
    { label: "Trails", value: trailCount, icon: PathIcon },
    accessLabel ? { label: "Access", value: accessLabel, icon: CableCarIcon } : null,
    stylesLabel ? { label: "Riding", value: stylesLabel, icon: PathIcon } : null,
  ].filter(Boolean);


  return (
    <div className="park-detail-page min-h-screen w-full min-w-0 overflow-x-clip bg-[#09090b] pb-20">
      <Seo
        title={`${park.name} — Bike park & trails | Bikeparks Finder`}
        description={seoDescription}
        path={canonicalPath}
        image={seoImage}
        structuredData={buildParkSchema(park, canonicalPath, seoImage)}
      />
      <Navbar scrollsWithPage brandVariant="mark" />

      {/* 1. Hero / BPF Fit */}
      <section
        data-detail-section="hero-fit"
        className={`relative w-full overflow-hidden ${
          hasRealHeroImage
            ? "h-[52vh] min-h-[430px]"
            : "h-[52vh] min-h-[430px] md:h-[40vh] md:min-h-[380px] md:max-h-[470px]"
        }`}
      >
        <img
          src={heroImage}
          alt={hasRealHeroImage ? park.name : `${park.name} default BPF cover`}
          data-testid={hasRealHeroImage ? undefined : "destination-hero-fallback"}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/40 to-[#09090b]/30" />

        <div className="absolute left-0 right-0 top-20 z-10">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6">
            <button
              onClick={handleBack}
              data-testid="back-btn"
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-black/60 px-4 py-2 text-sm font-bold text-white backdrop-blur-md transition-colors hover:bg-black/80"
            >
              <ArrowLeftIcon size={16} weight="bold" /> {translateLabel("Back")}
            </button>

            <a
              href="#community-media"
              data-testid="view-all-media"
              className="flex items-center gap-2 rounded-full border border-white/10 bg-black/60 px-4 py-2 text-sm font-bold text-white backdrop-blur-md transition-colors hover:bg-black/80"
            >
              <ImagesIcon size={17} weight="bold" /> {translateLabel("View media")}
            </a>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <div className="mx-auto max-w-7xl px-4 pb-6 md:px-6 md:pb-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-[#CCFF00] px-3 py-1 text-xs font-bold uppercase text-black">{translateLabel(park.type)}</span>
              <span
                data-testid="destination-status-badge"
                className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${STATUS_STYLES[status] || STATUS_STYLES.UNKNOWN}`}
              >
                {currentDestinationStatusLabel(status)}
              </span>
            </div>
            <h1 className="mt-3 max-w-4xl font-display text-[clamp(2.25rem,9.5vw,3rem)] font-black uppercase leading-[0.95] tracking-tight text-white [text-wrap:balance] md:text-6xl md:leading-none" data-testid="park-title">
              {park.name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm font-semibold text-zinc-300">
              {(park.city || park.country) && (
                <span className="flex items-center gap-1.5"><MapPinIcon size={16} className="text-[#CCFF00]" /> {[park.city, park.country].filter(Boolean).join(", ")}</span>
              )}
              {park.rating_count > 0 && (
                <span className="flex items-center gap-1.5"><StarIcon size={16} weight="fill" className="text-[#CCFF00]" /> {Number(park.rating).toFixed(1)} · {park.rating_count}</span>
              )}
            </div>
            {showRecommendationReason && (
              <div data-testid="destination-bpf-reason" className="mt-4 max-w-xl rounded-2xl border border-[#CCFF00]/30 bg-black/55 px-4 py-3 backdrop-blur-md">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#CCFF00]">{translateLabel("Why BPF highlights it")}</div>
                <p className="mt-1 text-sm font-semibold text-white">{recommendationReason}</p>
              </div>
            )}
            <div className="mt-5 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3">
              {directionsUrl ? (
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="navigate-btn"
                  className="flex items-center justify-center gap-2 rounded-full bg-[#CCFF00] px-4 py-3 text-sm font-bold text-black shadow-lg transition-transform active:scale-95 sm:px-6"
                >
                  <NavigationArrowIcon size={18} weight="fill" /> {translateLabel("Directions")}
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  data-testid="navigate-unavailable"
                  title={translateLabel("Primary access point is not available")}
                  className="flex cursor-not-allowed items-center justify-center gap-2 rounded-full border border-white/10 bg-black/45 px-4 py-3 text-sm font-bold text-zinc-500 sm:px-6"
                >
                  <NavigationArrowIcon size={18} /> {translateLabel("Directions unavailable")}
                </button>
              )}
              {park.website && (
                <a
                  href={park.website}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="website-btn"
                  className="flex items-center justify-center gap-2 rounded-full border border-white/15 bg-black/55 px-4 py-3 text-sm font-bold text-white backdrop-blur-md transition-colors hover:bg-black/75 sm:px-6"
                >
                  <GlobeIcon size={18} /> {translateLabel("Website")}
                </a>
              )}
              <ShareMenu payload={parkSharePayload(park)} />

              <button
                type="button"
                onClick={handleFav}
                data-testid="detail-fav-btn"
                aria-pressed={fav}
                aria-label={translateLabel(fav ? "Remove from Want to Ride" : "Add to Want to Ride")}
                className={`flex items-center justify-center gap-2 rounded-full border px-4 py-3 text-sm font-bold backdrop-blur-md transition-colors active:scale-95 sm:px-6 ${
                  fav
                    ? "border-[#FF4500] bg-[#FF4500] text-white"
                    : "border-white/15 bg-black/55 text-white hover:bg-black/75"
                }`}
              >
                <HeartIcon size={18} weight={fav ? "fill" : "regular"} /> {translateLabel(fav ? "Saved" : "Want to Ride")}
              </button>
              <button
                type="button"
                onClick={handleVisited}
                disabled={visitedBusy}
                data-testid="detail-visited-btn"
                aria-pressed={Boolean(destinationState?.visited)}
                aria-label={translateLabel(destinationState?.visited ? "Remove from Visited" : "Mark as Visited")}
                className={`flex items-center justify-center gap-2 rounded-full border px-4 py-3 text-sm font-bold backdrop-blur-md transition-colors active:scale-95 disabled:opacity-60 sm:px-6 ${
                  destinationState?.visited
                    ? "border-[#CCFF00] bg-[#CCFF00] text-black"
                    : "border-white/15 bg-black/55 text-white hover:bg-black/75"
                }`}
              >
                <CheckCircleIcon size={18} weight={destinationState?.visited ? "fill" : "regular"} />
                {translateLabel(visitedBusy ? "Saving…" : destinationState?.visited ? "Visited" : "Mark visited")}
              </button>
            </div>
          </div>
        </div>
      </section>

      <nav className="sticky top-0 z-[1100] border-y border-white/10 bg-[#09090b]/95 backdrop-blur-xl md:top-16 lg:static lg:z-auto lg:bg-[#09090b] lg:backdrop-blur-none" aria-label={translateLabel("Destination sections")}>
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 md:px-6">
          {destinationSections.map((section) => (
            <SectionNavLink
              key={section.id}
              section={section}
              onSelect={navigateToSection}
            />
          ))}
        </div>
      </nav>

      <div className="park-detail-content mx-auto w-full max-w-7xl min-w-0 px-4 py-6 md:px-6">
        <div className="park-detail-grid grid w-full min-w-0 grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1.65fr)_minmax(19rem,0.85fr)] lg:items-start lg:gap-6">

        {/* 2. Ride Overview */}
        <section id="overview" data-detail-section="ride-overview" className="order-1 min-w-0 scroll-mt-28 rounded-2xl border border-white/10 bg-[#121214] p-5 lg:col-start-1 lg:row-start-1 lg:p-6">
          <SectionHeading title={translateLabel("Ride Overview")} />
          {(park.description || park.summary) && (
            <p className="mb-5 max-w-3xl text-sm leading-relaxed text-zinc-300 sm:text-base">{park.description || park.summary}</p>
          )}
          <div className="grid min-w-0 grid-cols-[repeat(2,minmax(0,1fr))] gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 md:[grid-template-columns:repeat(auto-fit,minmax(9rem,1fr))]">
            {overview.map((o) => (
              <div key={translateLabel(o.label)} className="min-w-0 bg-[#121214] p-4">
                <div className="mb-2 flex items-center gap-1.5 text-[#CCFF00]"><o.icon size={16} weight="bold" /></div>
                <div className="label-tiny">{translateLabel(o.label)}</div>
                <div className="mt-1 break-words font-display text-base font-bold text-white">{translateLabel(o.value)}</div>
              </div>
            ))}
          </div>
        </section>

        <aside className="order-2 min-w-0 space-y-5 lg:col-start-2 lg:row-start-1 lg:row-span-5 lg:self-start">
          <section
            id="plan-your-ride"
            data-detail-section="current-conditions"
            className="scroll-mt-28 rounded-2xl border border-[#CCFF00]/15 bg-[linear-gradient(180deg,rgba(204,255,0,0.045),rgba(18,18,20,1)_22%)] p-5 shadow-[0_18px_45px_rgba(0,0,0,0.22)] lg:sticky lg:top-20"
          >
            <SectionHeading title={translateLabel("Plan Your Ride")} subtitle={translateLabel("Know the essentials before you leave.")} compact />

            <DestinationStatusTrustPanel
              destination={park}
              accessLabel={accessLabel}
            />

            <ParkDecisionSnapshot destination={park} />

            <div className="mt-4">
              <PlanThisRideButton destination={park} />
            </div>

            <div className="mt-4">
              <DestinationVisitDecisionPanel
                destination={park}
                accessLabel={accessLabel}
                directionsUrl={directionsUrl}
              />
            </div>

            <div className="mt-4 border-t border-white/[0.08]">
              <DestinationWeatherForecast destinationId={park.id} compact />
            </div>

            <div className="mt-4">
              <DestinationPracticalInfo
                destination={park}
                accessPointLabel={accessPointLabel}
                accessLabel={accessLabel}
                directionsUrl={directionsUrl}
                compact
              />
            </div>
          </section>
          <DestinationEvents destinationId={park.id} />
        </aside>

        <div className="order-3 min-w-0 space-y-5 lg:col-start-1 lg:row-start-2">

        {/* 3. Map & Trails */}
        {MAP_TRAILS_ENABLED && (
          <section id="trails" data-detail-section="map-trails" className="scroll-mt-28 rounded-2xl border border-white/10 bg-[#121214] p-5 lg:p-6">
            <SectionHeading title={translateLabel("Map & Trails")} subtitle={translateLabel("Select a trail in the map or list to keep both views synchronized.")} />

          <div className="mb-4 flex flex-wrap gap-2" data-testid="destination-ride-glance">
            {glanceItems.map((item) => {
              const toneClass = {
                lime: "border-[#CCFF00]/25 bg-[#CCFF00]/[0.07] text-[#CCFF00]",
                red: "border-red-400/20 bg-red-400/[0.06] text-red-300",
                green: "border-emerald-400/20 bg-emerald-400/[0.06] text-emerald-300",
                good: "border-emerald-400/20 bg-emerald-400/[0.06] text-emerald-300",
                warn: "border-amber-400/20 bg-amber-400/[0.06] text-amber-300",
                alert: "border-red-400/20 bg-red-400/[0.06] text-red-300",
                neutral: "border-white/10 bg-white/[0.04] text-zinc-200",
                muted: "border-white/[0.07] bg-white/[0.025] text-zinc-500",
              }[item.tone] || "border-white/10 bg-white/[0.04] text-zinc-300";
              return (
                <span key={translateLabel(item.label)} className={`rounded-full border px-3 py-1.5 text-[11px] font-bold ${toneClass}`}>
                  {translateLabel(item.label)}
                </span>
              );
            })}
          </div>

          <div className="mb-3 min-w-0 overflow-x-auto"><DifficultyLegend distribution={park.difficulty_distribution} /></div>
          <div className="h-[330px] min-w-0 overflow-hidden rounded-2xl border border-white/10 sm:h-[400px] lg:h-[460px]" data-testid="park-trail-map">
            <MapView
              parks={[park]}
              trails={trails}
              selectedId={park.id}
              selectedTrailId={trailInteraction.selectedTrailId}
              hoveredTrailId={trailInteraction.hoveredTrailId}
              focusedTrailId={trailInteraction.selectedTrailId}
              trailFocusKey={trailInteraction.focusRequest}
              onTrailHover={handleTrailHover}
              onTrailSelect={(trailId) => handleTrailSelect(trailId, "map")}
              fitTrails
              zoom={12}
            />
          </div>

          <div className="mb-4 mt-8 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="label-tiny text-[#CCFF00]">{translateLabel("Trail catalog")}</div>
              <h3 className="mt-1 font-display text-2xl font-bold uppercase text-white sm:text-3xl">{translateLabel("Choose your line")}</h3>
              <p className="mt-1 text-xs text-zinc-500">{translateLabel("Browse by riding character, then focus any trail on the map.")}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1.5 text-xs font-bold text-zinc-300">
                {translateLabel("{shown} of {total} trails", { shown: trails.length, total: park.trail_count ?? 0 })}
              </span>
              <SuggestTrail destinationId={park.id} destinationName={park.name} />
            </div>
          </div>

          <div className="-mx-2 mb-5 rounded-2xl border border-white/[0.07] bg-[#121214] p-2 sm:mx-0">
            <div className="bpf-scrollbar-hidden overflow-x-auto pb-1">
              <div className="flex min-w-max gap-2" data-testid="trail-catalog-filters">
                <button
                  type="button"
                  onClick={() => setCatalogGroupFilter("all")}
                  data-testid="trail-filter-group-all"
                  className={`flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-extrabold transition-colors ${
                    catalogGroupFilter === "all"
                      ? "bg-[#CCFF00] text-black"
                      : "bg-white/[0.03] text-zinc-300 hover:bg-white/[0.07] hover:text-white"
                  }`}
                >
                  {translateLabel("All")}
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                    catalogGroupFilter === "all" ? "bg-black/10" : "bg-white/[0.07] text-zinc-400"
                  }`}>{difficultyFilteredTrails.length}</span>
                </button>
                {catalogFilterOptions.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => setCatalogGroupFilter(group.id)}
                    data-testid={`trail-filter-group-${group.id}`}
                    className={`flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-bold transition-colors ${
                      catalogGroupFilter === group.id
                        ? "bg-white text-black"
                        : "bg-white/[0.03] text-zinc-300 hover:bg-white/[0.07] hover:text-white"
                    }`}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: group.color }} />
                    {translateLabel(group.label)}
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                      catalogGroupFilter === group.id ? "bg-black/10" : "bg-white/[0.07] text-zinc-400"
                    }`}>{catalogCounts[group.id]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="bpf-scrollbar-hidden mt-2 flex gap-2 overflow-x-auto pb-0.5">
              <span className="flex shrink-0 items-center pr-1 text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">{translateLabel("Difficulty")}</span>
              {DIFFS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDiffFilter((f) => f.includes(d) ? f.filter((x) => x !== d) : [...f, d])}
                  data-testid={`trail-filter-diff-${d}`}
                  className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${
                    diffFilter.includes(d)
                      ? "border-white bg-white text-black"
                      : "border-white/[0.08] bg-white/[0.03] text-zinc-300 hover:border-white/20 hover:bg-white/[0.06]"
                  }`}
                >
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: DIFFICULTY[d].color }} />
                  {DIFFICULTY[d].label}
                </button>
              ))}
            </div>
          </div>

          {selectedTrail && (
            <div data-testid="selected-trail-summary" className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-[#CCFF00]/20 bg-[#CCFF00]/[0.04] px-4 py-2.5 text-xs text-zinc-400">
              <span><span className="font-bold text-[#CCFF00]">{translateLabel("Focused on map:")}</span> {selectedTrail.name}</span>
              <Link to={trailPath(selectedTrail)} className="shrink-0 font-bold text-white hover:text-[#CCFF00]">{translateLabel("Open trail →")}</Link>
            </div>
          )}

          {trailGroups.length > 0 ? (
            <TrailCatalog
              groups={trailGroups}
              activeGroupFilter={catalogGroupFilter}
              compact={(park.trail_count ?? 0) <= 3 && catalogGroupFilter === "all"}
              selectedTrailId={trailInteraction.selectedTrailId}
              hoveredTrailId={trailInteraction.hoveredTrailId}
              onHover={handleTrailHover}
              onSelect={(trailId) => handleTrailSelect(trailId, "list")}
              registerRow={(trailId, node) => {
                if (node) trailRowRefs.current[trailId] = node;
                else delete trailRowRefs.current[trailId];
              }}
            />
          ) : (
            <div className="rounded-2xl border border-dashed border-white/10 bg-black/10 px-5 py-10 text-center">
              <p className="text-sm font-semibold text-zinc-300">{translateLabel("No trails match those filters.")}</p>
              <p className="mt-1 text-xs text-zinc-500">{translateLabel("Try another trail family or reset difficulty filters.")}</p>
              {(catalogGroupFilter !== "all" || diffFilter.length > 0) && (
                <button
                  type="button"
                  onClick={() => {
                    setCatalogGroupFilter("all");
                    setDiffFilter([]);
                  }}
                  className="mt-4 rounded-full bg-[#CCFF00] px-4 py-2 text-xs font-bold text-black"
                  data-testid="trail-filter-reset"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
          </section>
        )}

        {/* 5. Media */}
        <CommunityMediaGallery destinationId={park.id} onCoverChanged={(media) => setCoverMediaUrl(media.url)} compact />

        {/* 7. Reviews */}
        <DestinationReviews destinationId={park.id} compact />

        {/* 8. Community feedback */}
        <DestinationCommunityActions destination={park} compact includeEvents={false} />
        </div>
        </div>
      </div>
    </div>
  );
}

function SectionNavLink({ section, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(section.id)}
      className="shrink-0 border-b-2 border-transparent px-4 py-3 text-xs font-bold text-zinc-400 outline-none transition-colors hover:border-[#CCFF00] hover:text-white focus-visible:border-[#CCFF00] focus-visible:text-white"
    >
      {translateLabel(section.label)}
    </button>
  );
}

function SectionHeading({ title, subtitle, compact = false }) {
  return (
    <div className="mb-4">
      <h2 className={`font-display font-bold uppercase text-white ${compact ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"}`}>{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-zinc-400">{subtitle}</p>}
    </div>
  );
}

